export interface ServiceTier {
  id: string;
  name: string;
  price: number;
  priceId: string;
  description: string;
  features: string[];
  requiredIntegrations: Integration[];
}

export interface Integration {
  id: string;
  name: string;
  type: 'social' | 'marketing' | 'dns' | 'other';
  description: string;
  setupInstructions: string;
}

export interface ServiceType {
  id: string;
  name: string;
  description: string;
  benefits: string[];
}

export interface AddOn {
  id: string;
  name: string;
  description: string;
  type: 'web' | 'automation' | 'communication';
  price: number;
  isOneTime: boolean;
  status: 'inactive' | 'active';
  fulfillmentPath?: string;
  note?: string;
}

export interface Client {
  id: string;
  businessName: string;
  contactName: string;
  email: string;
  phone: string;
  address: string;
  password?: string;
  confirmPassword?: string;
  selectedServiceType?: string;
  selectedTier: string;
  selectedAddOns: string[];
  integrationStatus: Record<string, 'pending' | 'completed'>;
  paymentStatus: 'pending' | 'completed';
}

export interface FormField {
  name: string;
  label: string;
  type: 'text' | 'email' | 'password' | 'url' | 'number';
  required: boolean;
  defaultValue?: string;
  hint?: string;
}

export interface CredentialsFormConfig {
  title: string;
  fields: FormField[];
}

export interface OnboardingState {
  currentStep: number;
  client: Partial<Client>;
  selectedServiceTypes: ServiceType[];
  selectedTier: ServiceTier | null;
}

export const SERVICE_TYPES: ServiceType[] = [
  {
    id: 'repeat-referral-system',
    name: 'Repeat & Referral System',
    description: 'Timing-based customer engagement that queues or sends approved follow-up, referral, and win-back outreach when customers are most likely to respond.',
    benefits: [
      'Timing-based outreach when customers are ready',
      'Referral request timing and approval routing',
      'Win-back campaigns for lapsed customers',
      'Built-in suppression to avoid over-contacting',
      'Loyalty moment triggers for repeat business'
    ]
  },
  {
    id: 'content-engine',
    name: 'Content Engine',
    description: 'Content production system that creates website articles and short-form video packages, then queues or releases them through configured approval and publishing rules.',
    benefits: [
      'Website articles produced from service, area, and seasonal priorities',
      'Short-form video packages prepared for approved channels',
      'Batched website publishing to avoid spammy release patterns',
      'Configured release rules for website publishing and approved social channels',
      'Manual upload and approved assets can enrich output',
      'Brand voice and style consistency'
    ]
  },
  {
    id: 'proof-of-work',
    name: 'Proof-of-Work Auto Publisher',
    description: 'Publishing system that turns completed job photos into location-optimized posts that queue or publish to approved socials and Google Business Profile.',
    benefits: [
      'Job photos become queued or approved posts',
      'Location context applied automatically',
      'Batched scheduling to avoid flooding feeds',
      'No manual caption writing needed',
      'Perfect for field service teams',
      'Showcases your work consistently'
    ]
  },
  {
    id: 'nap-entity-boost',
    name: 'Local Authority Engine',
    description: 'Local authority system for connected-source audits, NAP consistency, Website Schema.org evidence, eligible remediation, verification checks, and owner-action routing.',
    benefits: [
      'Discovers local authority and NAP issues across the web',
      'Routes eligible fixes through activated connectors or provider paths',
      'Improves Google Business Profile, website schema, and AI entity consistency where access allows',
      'Creates step-by-step client actions for publisher changes that cannot be automated',
      'Runs verification checks after remediation',
      'Tier level and billing period are chosen later in the dashboard before Stripe payment starts'
    ]
  },
  {
    id: 'ai-audit',
    name: 'AI Audit',
    description: 'Monthly AI visibility and citation readiness audit that turns crawl, answer-engine, source-quality, and optimization signals into a clear score, quick metrics, and owner actions.',
    benefits: [
      'AI visibility and citation readiness checks',
      'Answer-engine source quality review',
      'Quick overview score and priority metrics',
      'Owner-action recommendations',
      'Dashboard and email reporting path',
      'Discounted when paired with SEO & GBP Audit'
    ]
  },
  {
    id: 'seo-gbp-audit',
    name: 'SEO & GBP Audit',
    description: 'Monthly website SEO and Google Business Profile audit that reviews search, local profile, crawl, performance, and owner-action signals.',
    benefits: [
      'Website SEO and crawl health review',
      'Google Business Profile readiness checks',
      'Search Console and GA4 reporting path',
      'Quick overview score and priority metrics',
      'Monthly change tracking path',
      'Discounted when paired with AI Audit'
    ]
  },
  {
    id: 'customer-intelligence',
    name: 'Customer Intelligence',
    description: 'Customer data intelligence that turns connected customer, job, referral, and attribution data into ranked opportunities, protected community leads, and measurable growth signals. Requires active Repeat & Referral.',
    benefits: [
      'Requires active Repeat & Referral',
      'Scores customers for rebook, referral, churn, and upsell timing',
      'Surfaces actionable signals before customers go quiet',
      'Feeds R&R with approval-gated outreach opportunities',
      'Tracks attribution from signals to revenue and referrals',
      'Keeps network lead source identity private',
      'Requires customer data connection before activation'
    ]
  },
];

export const ADD_ONS: AddOn[] = [
  // Web Presence
  {
    id: 'basic-landing',
    name: 'Basic Landing Page',
    description: 'Simple, effective landing page for your business',
    type: 'web',
    price: 250,
    isOneTime: true,
    status: 'inactive',
    fulfillmentPath: 'not_configured',
    note: 'One Time Expense (OTE)'
  },
  {
    id: 'full-landing',
    name: 'Fully Functionality Landing Page',
    description: 'Feature-rich landing page with advanced functionality',
    type: 'web',
    price: 500,
    isOneTime: true,
    status: 'inactive',
    fulfillmentPath: 'not_configured',
    note: 'One Time Expense (OTE)'
  },
  {
    id: 'basic-website',
    name: 'Basic Website',
    description: 'Professional multi-page website',
    type: 'web',
    price: 750,
    isOneTime: true,
    status: 'inactive',
    fulfillmentPath: 'not_configured',
    note: 'One Time Expense (OTE)'
  },
  {
    id: 'full-website',
    name: 'Fully Functionality Website',
    description: 'Complete website solution with advanced features',
    type: 'web',
    price: 1000,
    isOneTime: true,
    status: 'inactive',
    fulfillmentPath: 'not_configured',
    note: 'One Time Expense (OTE) - Can range up to $5000 based on requirements'
  },
  // Customer Communication
  {
    id: 'ai-text',
    name: 'AI Text Assistant',
    description: 'Text-based AI assistant using your business data',
    type: 'communication',
    price: 1000,
    isOneTime: false,
    status: 'inactive',
    fulfillmentPath: 'not_configured',
    note: 'Basic AI CRM: Answers based on client database and documentation'
  },
  {
    id: 'ai-voice',
    name: 'AI Voice Assistant',
    description: 'Voice-based AI assistant using your business data',
    type: 'communication',
    price: 1000,
    isOneTime: false,
    status: 'inactive',
    fulfillmentPath: 'not_configured',
    note: 'Basic AI CRM: Answers based on client database and documentation'
  },
  {
    id: 'ai-combined',
    name: 'Combined AI Assistant',
    description: 'Text and voice AI assistant using your business data',
    type: 'communication',
    price: 1500,
    isOneTime: false,
    status: 'inactive',
    fulfillmentPath: 'not_configured',
    note: 'Basic AI CRM: Combined voice and text assistant'
  }
];
